import { cn } from '@lib/utils';
import * as React from 'react';

type TextareaProps = React.ComponentProps<'textarea'> & {
  containerClassName?: string;
  generateButton?: React.ReactNode;
  hasError?: boolean;
};

/**
 * Password-manager autofill is opted-out by default. See note in `input.tsx`.
 */
const AUTOFILL_IGNORE_DEFAULTS = {
  autoComplete: 'off',
  'data-1p-ignore': 'true',
  'data-form-type': 'other',
  'data-lpignore': 'true',
} as const;

const Textarea = (props: TextareaProps) => {
  const { className, containerClassName, generateButton, hasError, ref, ...otherProps } = props;
  const ariaInvalid = hasError || otherProps['aria-invalid'];
  const [hasScrollbar, setHasScrollbar] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const setTextareaRef = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const checkScrollbar = React.useCallback(() => {
    if (!generateButton) return;
    const textarea = textareaRef.current;

    if (textarea) {
      const hasVerticalScrollbar = textarea.scrollHeight > textarea.clientHeight;
      setHasScrollbar(hasVerticalScrollbar);
    }
  }, [generateButton]);

  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !generateButton) return;

    checkScrollbar();

    let resizeObserver: ResizeObserver | null = null;
    const hasResizeObserver = typeof ResizeObserver !== 'undefined';

    if (hasResizeObserver) {
      resizeObserver = new ResizeObserver(checkScrollbar);
      resizeObserver.observe(textarea);
    } else {
      window.addEventListener('resize', checkScrollbar);
    }

    const handleInput = () => {
      setTimeout(checkScrollbar, 0);
    };

    textarea.addEventListener('input', handleInput);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', checkScrollbar);
      }
      textarea.removeEventListener('input', handleInput);
    };
  }, [checkScrollbar, generateButton]);

  return (
    <div className={cn('relative w-full', containerClassName)}>
      <textarea
        aria-invalid={ariaInvalid}
        className={cn(
          'min-h-20 w-full rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-base text-gray-600',
          'box-border md:text-sm placeholder:text-gray-400',
          'focus-visible:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
          'aria-invalid:border-red-500 aria-invalid:focus-visible:border-red-500 aria-invalid:focus-visible:ring-red-200',
          'disabled:cursor-not-allowed disabled:opacity-50',
          generateButton && 'pr-8',
          className,
        )}
        ref={setTextareaRef}
        {...AUTOFILL_IGNORE_DEFAULTS}
        {...otherProps}
      />
      {generateButton && (
        <div className={cn('absolute top-1.5', hasScrollbar ? 'right-4' : 'right-1.5')}>{generateButton}</div>
      )}
    </div>
  );
};
Textarea.displayName = 'Textarea';

export { Textarea };
